import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../../utils/dateUtils';
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import {
    Package,
    Plus,
    Search,
    Filter,
    Eye,
    Edit,
    Trash2,
    CheckCircle,
    Clock,
    XCircle,
    ArrowLeft,
    ChevronLeft,
    ChevronRight,
    MoreVertical,
} from 'lucide-react';
import {
    useGetDealerListingsQuery,
    useDeleteDealerListingMutation,
    DealerListing,
} from '../../store/slices/apiSlice';
import { toast } from 'react-hot-toast';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Card } from '../../components/ui/Card';

const DealerListings: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { user, token } = useSelector((state: RootState) => state.auth);
    
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [statusFilter, setStatusFilter] = useState<'all' | 'draft' | 'active' | 'inactive' | 'sold'>('all');
    const [approvalFilter, setApprovalFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
    const [searchTerm, setSearchTerm] = useState('');

    // Initialize filters from URL params
    useEffect(() => {
        const approvalParam = searchParams.get('approval_status');
        const statusParam = searchParams.get('status');
        if (approvalParam && ['pending', 'approved', 'rejected'].includes(approvalParam)) {
            setApprovalFilter(approvalParam as any);
        }
        if (statusParam && ['draft', 'active', 'inactive', 'sold'].includes(statusParam)) {
            setStatusFilter(statusParam as any);
        }
    }, [searchParams]);

    const { data, isLoading, error, refetch } = useGetDealerListingsQuery({
        page: currentPage,
        limit: itemsPerPage,
        status: statusFilter === 'all' ? undefined : statusFilter,
        approval_status: approvalFilter === 'all' ? undefined : approvalFilter,
        search: searchTerm || undefined,
    }, {
        skip: !token,
    });

    const [deleteListing, { isLoading: isDeleting }] = useDeleteDealerListingMutation();

    const listings = data?.data?.listings || [];
    const pagination = data?.data?.pagination || {
        current_page: 1,
        total_pages: 1,
        total_records: 0,
    };

    const handleDelete = async (listingId: number) => {
        if (!window.confirm('Are you sure you want to delete this listing?')) {
            return;
        }

        try {
            await deleteListing(listingId).unwrap();
            toast.success('Listing deleted successfully');
            refetch();
        } catch (error: any) {
            toast.error(error?.data?.message || 'Failed to delete listing');
        }
    };

    const getApprovalStatusBadge = (status: string) => {
        switch (status) {
            case 'approved':
                return <Badge variant="success" className="text-xs">Approved</Badge>;
            case 'pending':
                return <Badge variant="warning" className="text-xs">Pending</Badge>;
            case 'rejected':
                return <Badge variant="danger" className="text-xs">Rejected</Badge>;
            default:
                return null;
        }
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'active':
                return <Badge variant="success" className="text-xs">Active</Badge>;
            case 'sold':
                return <Badge className="text-xs bg-purple-100 text-purple-800">Sold</Badge>;
            case 'draft':
                return <Badge variant="secondary" className="text-xs">Draft</Badge>;
            case 'inactive':
                return <Badge variant="secondary" className="text-xs">Inactive</Badge>;
            default:
                return null;
        }
    };

    // Format date helper
    const formatDate = (dateString: string | Date | null | undefined) => formatDateUtil(dateString);;

    // Handle search with debounce
    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchTerm !== undefined) {
                setCurrentPage(1);
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    if (!user || user.role !== 'DEALER') {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-red-600 text-sm">Please log in as a dealer</div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-red-600 text-sm">Failed to load listings</div>
            </div>
        );
    }

    return (
        <div className="space-y-6 max-w-7xl mx-auto px-4 py-6 bg-white">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/dealer/dashboard')}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">My Listings</h1>
                        <p className="text-sm text-gray-600 mt-1">Manage your marketplace listings</p>
                    </div>
                </div>
                <Button
                    onClick={() => navigate('/dealer/listings/create')}
                    className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
                >
                    <Plus className="w-4 h-4" />
                    <span>Create New Listing</span>
                </Button>
            </div>

            {/* Filters and Search */}
            <Card className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <div className="relative md:col-span-2">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by title or description..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>
                    <div>
                        <select
                            value={statusFilter}
                            onChange={(e) => {
                                setStatusFilter(e.target.value as any);
                                setCurrentPage(1);
                            }}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                        >
                            <option value="all">All Status</option>
                            <option value="draft">Draft</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                            <option value="sold">Sold</option>
                        </select>
                    </div>
                    <div>
                        <select
                            value={approvalFilter}
                            onChange={(e) => {
                                setApprovalFilter(e.target.value as any);
                                setCurrentPage(1);
                            }}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                        >
                            <option value="all">All Approval</option>
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                        </select>
                    </div>
                    <div className="flex items-center gap-2">
                        <Filter className="w-5 h-5 text-gray-400" />
                        <span className="text-sm text-gray-600 font-medium">
                            {pagination.total_records} listings
                        </span>
                    </div>
                </div>
            </Card>

            {/* Listings Table */}
            {listings.length === 0 ? (
                <Card className="p-12 text-center">
                    <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No Listings Found</h3>
                    <p className="text-gray-600 mb-4">
                        {searchTerm || statusFilter !== 'all' || approvalFilter !== 'all'
                            ? 'Try adjusting your filters'
                            : 'Create your first listing to get started'}
                    </p>
                    {!searchTerm && statusFilter === 'all' && approvalFilter === 'all' && (
                        <Button
                            onClick={() => navigate('/dealer/listings/create')}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Create Listing
                        </Button>
                    )}
                </Card>
            ) : (
                <Card className="overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Image</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Title</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Price</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Location</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Status</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Approval</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Created</th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {listings.map((listing) => (
                                    <tr key={listing.listing_id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                                                {listing.images && listing.images.length > 0 ? (
                                                    <img
                                                        src={listing.images.find(img => img.is_primary)?.image_path || listing.images[0].image_path}
                                                        alt={listing.title}
                                                        className="w-full h-full object-cover"
                                                    />
                                                ) : (
                                                    <Package className="w-8 h-8 text-gray-400" />
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="max-w-xs">
                                                <p className="text-sm font-semibold text-gray-900 truncate">
                                                    {listing.title}
                                                </p>
                                                <p className="text-xs text-gray-500 truncate mt-1">
                                                    {listing.description}
                                                </p>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <p className="text-sm font-bold text-gray-900">
                                                {listing.currency_symbol || '$'}{listing.price.toLocaleString()}
                                            </p>
                                        </td>
                                        <td className="px-4 py-3">
                                            <p className="text-sm text-gray-600">
                                                {listing.location || 'N/A'}
                                            </p>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            {getStatusBadge(listing.status)}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            {getApprovalStatusBadge(listing.approval_status)}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <p className="text-xs text-gray-500">
                                                {formatDate(listing.created_at)}
                                            </p>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => navigate(`/dealer/listings/${listing.listing_id}`)}
                                                    className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                    title="View"
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => navigate(`/dealer/listings/${listing.listing_id}/edit`)}
                                                    className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                    title="Edit"
                                                >
                                                    <Edit className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(listing.listing_id)}
                                                    disabled={isDeleting}
                                                    className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                                                    title="Delete"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            )}

            {/* Pagination */}
            {pagination.total_pages > 0 && (
                <Card className="p-4">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                        {/* Items per page selector */}
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">Show:</span>
                            <select
                                value={itemsPerPage}
                                onChange={(e) => {
                                    setItemsPerPage(Number(e.target.value));
                                    setCurrentPage(1);
                                }}
                                className="px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-sm"
                            >
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                            <span className="text-sm text-gray-600">per page</span>
                        </div>

                        {/* Pagination controls */}
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setCurrentPage(1)}
                                disabled={currentPage === 1}
                                className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                First
                            </button>
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                disabled={currentPage === 1}
                                className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                <ChevronLeft className="w-5 h-5" />
                            </button>
                            
                            {/* Page numbers */}
                            <div className="flex items-center gap-1">
                                {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                                    let pageNum;
                                    if (pagination.total_pages <= 5) {
                                        pageNum = i + 1;
                                    } else if (currentPage <= 3) {
                                        pageNum = i + 1;
                                    } else if (currentPage >= pagination.total_pages - 2) {
                                        pageNum = pagination.total_pages - 4 + i;
                                    } else {
                                        pageNum = currentPage - 2 + i;
                                    }
                                    
                                    return (
                                        <button
                                            key={pageNum}
                                            onClick={() => setCurrentPage(pageNum)}
                                            className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                                                currentPage === pageNum
                                                    ? 'bg-blue-600 text-white font-semibold'
                                                    : 'border border-gray-300 hover:bg-gray-50 text-gray-700'
                                            }`}
                                        >
                                            {pageNum}
                                        </button>
                                    );
                                })}
                            </div>

                            <button
                                onClick={() => setCurrentPage(prev => Math.min(pagination.total_pages, prev + 1))}
                                disabled={currentPage === pagination.total_pages}
                                className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                <ChevronRight className="w-5 h-5" />
                            </button>
                            <button
                                onClick={() => setCurrentPage(pagination.total_pages)}
                                disabled={currentPage === pagination.total_pages}
                                className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                Last
                            </button>
                        </div>

                        {/* Page info */}
                        <div className="text-sm text-gray-600">
                            Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, pagination.total_records)} of {pagination.total_records} listings
                        </div>
                    </div>
                </Card>
            )}
        </div>
    );
};

export default DealerListings;
